import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../../hooks/useApi';
import { getKbEntry, loadBrandless, loadKbGroups, saveBrands, saveKbEntry } from '../../../../services/feedback';
import { errorText } from './feedbackUi';

const PAGE = 50;

export const LEVELS = [
    { id: 'model', label: 'Модели', hint: 'Знания, общие для всех товаров одной модели.' },
    { id: 'cat1', label: 'Категории 1 ур.', hint: 'Самая широкая категория Ozon.' },
    { id: 'cat2', label: 'Категории 2 ур.', hint: 'Подкатегория.' },
    { id: 'cat3', label: 'Категории 3 ур.', hint: 'Тип товара.' },
    { id: 'brand', label: 'Бренды', hint: 'Знания о бренде и его продукции.' },
    { id: 'shop', label: 'Магазин', hint: 'Доставка, возврат, гарантия, тон общения: всё, что верно для любого товара магазина.' },
];

function EntryEditor({ accountId, scope, entryKey, title, onSaved, onClose }) {
    const { request } = useApi();
    const [facts, setFacts] = useState('');
    const [faq, setFaq] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        let cancelled = false;
        setLoaded(false);
        setMessage('');
        getKbEntry({ request, accountId, scope, key: entryKey })
            .then((value) => {
                if (cancelled) return;
                setFacts(value.custom_facts || '');
                setFaq(value.faq || []);
                setLoaded(true);
            })
            .catch((error) => !cancelled && setMessage(errorText(error)));
        return () => { cancelled = true; };
    }, [request, accountId, scope, entryKey]);

    const updateFaq = (index, field, value) => setFaq(faq.map((pair, position) => (position === index ? { ...pair, [field]: value } : pair)));
    const save = async () => {
        try {
            await saveKbEntry({ request, accountId, scope, key: entryKey, customFacts: facts, faq });
            setMessage('Сохранено');
            onSaved();
        } catch (error) {
            setMessage(errorText(error));
        }
    };

    if (!loaded) return <div className="fb-card"><span className="fb-muted">{message || 'Загрузка…'}</span></div>;
    return (
        <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="fb-row fb-spread">
                <h4 style={{ margin: 0 }}>{title}</h4>
                {onClose && <button type="button" onClick={onClose}>Закрыть</button>}
            </div>
            <textarea className="fb-textarea" rows={5} maxLength={4000} value={facts} onChange={(event) => setFacts(event.target.value)}
                placeholder="Что верно для всех товаров этой группы: состав, срок годности, для кого, что нельзя обещать…" />
            <div className="fb-faq">
                <div className="fb-muted">Частые вопросы и утверждённые ответы</div>
                {faq.map((pair, index) => (
                    <div className="fb-faq__row" key={index}>
                        <input className="fb-input" placeholder="Вопрос" value={pair.q} onChange={(event) => updateFaq(index, 'q', event.target.value)} />
                        <input className="fb-input" placeholder="Ответ магазина" value={pair.a} onChange={(event) => updateFaq(index, 'a', event.target.value)} />
                        <button type="button" onClick={() => setFaq(faq.filter((_, position) => position !== index))}>Убрать</button>
                    </div>
                ))}
                <div><button type="button" onClick={() => setFaq([...faq, { q: '', a: '' }])}>Добавить вопрос</button></div>
            </div>
            <div className="fb-actions">
                <button type="button" className="primary" onClick={save}>Сохранить</button>
                {message && <span className="fb-note">{message}</span>}
            </div>
        </div>
    );
}

function BrandAttach({ accountId, onChanged }) {
    const { request } = useApi();
    const [data, setData] = useState({ total: 0, items: [], brands: [] });
    const [query, setQuery] = useState('');
    const [picked, setPicked] = useState({});
    const [brand, setBrand] = useState('');
    const [paste, setPaste] = useState('');
    const [message, setMessage] = useState('');

    const reload = useCallback(async () => {
        try {
            setData(await loadBrandless({ request, accountId, query: query.trim() }));
        } catch (error) {
            setMessage(errorText(error));
        }
    }, [request, accountId, query]);
    useEffect(() => { reload(); }, [reload]);

    const apply = async (pairs) => {
        if (!pairs.length) return;
        try {
            const result = await saveBrands({ request, accountId, pairs });
            setMessage(`Привязано товаров: ${result.changed}${result.skipped ? `, не найдено в ассортименте: ${result.skipped}` : ''}`);
            setPicked({});
            setPaste('');
            await reload();
            onChanged();
        } catch (error) {
            setMessage(errorText(error));
        }
    };
    const chosen = Object.keys(picked).filter((sku) => picked[sku]);
    const parsePaste = () => paste.split('\n').map((line) => line.split(/[;\t]/).map((part) => part.trim()))
        .filter((parts) => parts[0] && parts[1]).map(([sku, name]) => ({ sku, brand: name }));

    return (
        <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h4 style={{ margin: 0 }}>Товары без бренда: {data.total}</h4>
            <div className="fb-muted">Привязка виртуальная: в Ozon ничего не меняется, она нужна только чтобы знания бренда попадали в ответы.</div>
            <div className="fb-row">
                <input className="fb-input" placeholder="Поиск по названию, артикулу или SKU" value={query} onChange={(event) => setQuery(event.target.value)} />
                <input className="fb-input" list="fb-brands" placeholder="Бренд для выбранных" value={brand} onChange={(event) => setBrand(event.target.value)} />
                <datalist id="fb-brands">{data.brands.map((name) => <option key={name} value={name} />)}</datalist>
                <button type="button" className="primary" disabled={!brand.trim() || !chosen.length}
                    onClick={() => apply(chosen.map((sku) => ({ sku, brand: brand.trim() })))}>Привязать выбранные ({chosen.length})</button>
            </div>
            <div className="fb-table-wrap">
                <table className="fb-table">
                    <thead><tr><th /><th>Артикул</th><th>SKU</th><th>Название</th></tr></thead>
                    <tbody>
                        {data.items.map((row) => (
                            <tr key={row.sku}>
                                <td><input type="checkbox" checked={Boolean(picked[row.sku])} onChange={(event) => setPicked({ ...picked, [row.sku]: event.target.checked })} /></td>
                                <td>{row.offer_id || '—'}</td><td>{row.sku}</td><td style={{ maxWidth: 360 }}>{row.name || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {data.items.length === 0 && <div className="fb-empty">Все товары с брендом.</div>}
            </div>
            <label>
                <div className="fb-muted" style={{ marginBottom: 4 }}>Таблицей: по строке на товар, «SKU;Бренд» (пустой бренд снимает привязку)</div>
                <textarea className="fb-textarea" rows={3} value={paste} onChange={(event) => setPaste(event.target.value)} placeholder={'2975370965;Мой бренд\n2975370021;Мой бренд'} />
            </label>
            <div className="fb-actions">
                <button type="button" disabled={!paste.trim()} onClick={() => apply(parsePaste())}>Привязать из таблицы</button>
                {message && <span className="fb-note">{message}</span>}
            </div>
        </div>
    );
}

export default function FeedbackKnowledgeLevels({ accountId, level }) {
    const { request } = useApi();
    const info = LEVELS.find((item) => item.id === level) || LEVELS[0];
    const [query, setQuery] = useState('');
    const [offset, setOffset] = useState(0);
    const [data, setData] = useState({ total: 0, filled: 0, items: [] });
    const [selected, setSelected] = useState(null);
    const [error, setError] = useState('');

    const reload = useCallback(async () => {
        if (level === 'shop') return;
        setError('');
        try {
            setData(await loadKbGroups({ request, accountId, scope: level, query: query.trim(), limit: PAGE, offset }));
        } catch (err) {
            setError(errorText(err));
        }
    }, [request, accountId, level, query, offset]);

    useEffect(() => { setSelected(null); setOffset(0); }, [level]);
    useEffect(() => { reload(); }, [reload]);

    if (level === 'shop') {
        return <EntryEditor accountId={accountId} scope="shop" entryKey="" title="Знания о магазине" onSaved={() => {}} />;
    }
    return (
        <div className="fb" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="fb-card">
                <div className="fb-row fb-spread">
                    <div className="fb-muted">{info.hint} Заполнено {data.filled} из {data.total}. В ответ попадают знания от товара к общему: товар, модель, категория, бренд, магазин.</div>
                    <input className="fb-input" placeholder="Поиск" value={query} onChange={(event) => { setQuery(event.target.value); setOffset(0); }} />
                </div>
            </div>
            {level === 'brand' && <BrandAttach accountId={accountId} onChanged={reload} />}
            {error && <div className="fb-card fb-chip--bad">{error}</div>}
            <div className={selected ? 'fb-two' : ''}>
                <div className="fb-table-wrap">
                    <table className="fb-table">
                        <thead><tr><th>{info.label.replace(/ ур\.$/, '')}</th><th className="fb-num">Товаров</th><th className="fb-num">Остаток</th><th>Знания</th></tr></thead>
                        <tbody>
                            {data.items.map((row) => (
                                <tr key={row.key || '_'} className="fb-clickable" onClick={() => setSelected(row)}>
                                    <td style={{ maxWidth: 420 }}>{row.label}</td>
                                    <td className="fb-num">{row.products}</td>
                                    <td className="fb-num">{row.stock ?? '—'}</td>
                                    <td>{row.filled ? <span className="fb-chip fb-chip--good">есть</span> : <span className="fb-chip">нет</span>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {data.items.length === 0 && <div className="fb-empty">Пока пусто. Для категорий нужны синхронизированные карточки и доступ к дереву категорий Ozon.</div>}
                </div>
                {selected && (selected.key || level !== 'brand') && (
                    <EntryEditor accountId={accountId} scope={level} entryKey={selected.key} title={selected.label}
                        onSaved={reload} onClose={() => setSelected(null)} />
                )}
            </div>
            {data.total > PAGE && (
                <div className="fb-row">
                    <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(offset - PAGE, 0))}>Назад</button>
                    <span className="fb-note">{offset + 1}–{Math.min(offset + PAGE, data.total)} из {data.total}</span>
                    <button type="button" disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}>Дальше</button>
                </div>
            )}
        </div>
    );
}
